import type { ReactNode } from "react";
import { PLANS, type Plan } from "../../shared/types.ts";
import { num } from "../lib/format.ts";
import { pickPlan } from "../lib/plan.ts";
import { Button, Logo, Monogram, cn } from "../ui";
import { Item, Reveal } from "./Reveal";
import { Check, Stars } from "./mocks";
import { Bar } from "./shapes";
import { fade, h2, wrap } from "./styles";

const quotes = [
  { name: "Priya Shah", role: "Product designer", result: "4 interviews from 46 applications.", text: "I swiped on the train home. By morning they were all out." },
  { name: "Marcus Okafor", role: "Backend engineer", result: "2 interviews in week one.", text: "It found jobs at companies I’d never have checked." },
  { name: "Elena Ruiz", role: "Data analyst", result: "3 interviews by Friday.", text: "Forty applications a night, and I never filled in a form." },
];

export function Proof() {
  return (
    <section className="band relative overflow-hidden py-20 md:py-28">
      <Bar tone="blue" delay={0.1} inView className="-left-14 top-16 hidden h-[400px] w-[120px] md:block" />
      <Bar tone="orange" flip delay={0.2} inView className="-bottom-16 -right-12 hidden h-[340px] w-[110px] md:block" />
      <div className={cn(wrap, "relative")}>
        <Reveal>
          <Item as="h2" className={cn(h2, "text-center")}>
            Trusted by <span className={fade}>people like you.</span>
          </Item>
        </Reveal>
        {/* Placeholder testimonials and initials avatars: replace with real quotes and photos before launch. */}
        <Reveal gap={0.1} className="mt-10 grid grid-cols-1 gap-4 md:mt-14 md:grid-cols-3">
          {quotes.map((q, i) => (
            <Item as="figure" key={q.name} className={cn("flex flex-col rounded-card bg-surface p-7 shadow-card", i === 1 && "md:-translate-y-4")}>
              <Stars />
              <p className="mt-5 font-display text-[24px] font-extrabold leading-tight tracking-[-0.03em] text-primary-ink text-pretty">{q.result}</p>
              <blockquote className="mt-3 flex-1 text-[16px] leading-relaxed text-ink-soft text-pretty">“{q.text}”</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-line pt-5">
                <Monogram name={q.name} logo={false} size="md" className="rounded-full" />
                <span>
                  <span className="block text-[15px] font-bold text-ink">{q.name}</span>
                  <span className="block text-[13px] text-muted">{q.role}</span>
                </span>
              </figcaption>
            </Item>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

const apps = (p: Plan) => `${num(PLANS[p].monthly)} applications a month`;

const plans: { id: Plan; cta: string; features: string[]; featured?: boolean }[] = [
  { id: "free", cta: "Start free", features: ["Every open role, ranked for you", apps("free"), "Your job search Sankey"] },
  { id: "autopilot", cta: "Start autopilot", features: [apps("autopilot"), "Every question answered for you", "Fit notes on every role"], featured: true },
  { id: "max", cta: "Go max", features: [apps("max"), "Everything in Autopilot"] },
];

/**
 * The three plan cards with Autopilot raised in the centre. `action` renders each card's button, given the landing's
 * label for it. `inView` reveals them on scroll, for the landing; the app shows them on mount and, with `leadOnPhone`,
 * stacks Autopilot first on phones.
 */
export function PlanCards({
  action,
  inView = true,
  leadOnPhone,
  className,
}: {
  action: (p: Plan, featured: boolean, cta: string) => ReactNode;
  inView?: boolean;
  leadOnPhone?: boolean;
  className?: string;
}) {
  return (
    <Reveal gap={0.1} inView={inView} className={cn("mx-auto grid max-w-[1040px] grid-cols-1 items-center gap-4 md:grid-cols-[1fr_1.12fr_1fr]", className)}>
      {plans.map((p) => (
        <Item
          key={p.id}
          className={cn(
            "relative flex flex-col overflow-hidden rounded-card p-7",
            leadOnPhone && p.featured && "-order-1 md:order-none",
            p.featured ? "bg-primary text-on-primary shadow-raised md:px-9 md:py-11" : "bg-surface text-ink shadow-card",
          )}
        >
          {p.featured ? (
            <>
              <Bar tone="orange" delay={0.35} inView={inView} className="-right-3 -top-6 h-[150px] w-[46px]" />
              <Bar tone="tint" flip delay={0.45} inView={inView} opacity={0.22} className="right-[40px] -top-10 h-[120px] w-[46px]" />
            </>
          ) : null}
          <h3 className={cn("relative text-[17px] font-bold", p.featured ? "text-on-primary" : "text-ink")}>{PLANS[p.id].name}</h3>
          <p className="relative mt-3 flex items-baseline gap-1">
            <span className={cn("font-display font-extrabold leading-none tracking-[-0.045em] tabular-nums", p.featured ? "text-[64px]" : "text-[56px]")}>${PLANS[p.id].price}</span>
            <span className={cn("text-[15px]", p.featured ? "text-on-primary/75" : "text-muted")}>/month</span>
          </p>
          <ul className={cn("relative mt-6 flex flex-col gap-3", p.featured && "border-t border-white/20 pt-6")}>
            {p.features.map((f) => (
              <li key={f} className={cn("flex items-start gap-2.5 text-[15px]", p.featured ? "font-semibold text-on-primary" : "text-ink-soft")}>
                <Check className={cn("mt-[3px]", p.featured ? "text-accent" : "text-primary")} />
                {f}
              </li>
            ))}
          </ul>
          {action(p.id, !!p.featured, p.cta)}
        </Item>
      ))}
    </Reveal>
  );
}

export function Pricing({ onStart }: { onStart: () => void }) {
  return (
    <section id="pricing" className={cn(wrap, "py-24 md:py-36")}>
      <Reveal>
        <Item as="h2" className={cn(h2, "text-center")}>
          Choose <span className={fade}>what’s right for you.</span>
        </Item>
      </Reveal>
      <PlanCards
        className="mt-10 md:mt-14"
        action={(p, featured, cta) => (
          <Button
            variant={featured ? "accent" : "secondary"}
            size={featured ? "lg" : "md"}
            className="relative mt-8 w-full"
            onClick={() => {
              pickPlan(p);
              onStart();
            }}
          >
            {cta}
          </Button>
        )}
      />
    </section>
  );
}

export function CtaBand({ onStart }: { onStart: () => void }) {
  return (
    <section className={cn(wrap, "pb-12")}>
      <div className="relative overflow-hidden rounded-card bg-primary px-6 py-20 text-center md:py-32">
        <Bar tone="tint" delay={0.1} inView opacity={0.16} className="-left-6 -top-10 h-[260px] w-[80px] md:h-[380px] md:w-[120px]" />
        <Bar tone="orange" delay={0.25} inView className="-bottom-12 right-6 h-[220px] w-[70px] md:right-16 md:h-[340px] md:w-[110px]" />
        <Bar tone="tint" flip delay={0.35} inView opacity={0.18} className="-bottom-16 right-[128px] hidden h-[260px] w-[110px] md:block" />
        <Reveal className="relative">
          <Item as="h2" className="mx-auto max-w-[13ch] font-display text-[clamp(3rem,7vw,6rem)] font-extrabold leading-[0.98] tracking-[-0.045em] text-balance text-white">
            Go to bed. <span className="text-cream">Wake up applied.</span>
          </Item>
          <Item>
            <Button size="lg" variant="secondary" className="mt-9 text-primary-ink ring-0 shadow-raised hover:bg-cream-soft" onClick={onStart}>
              Start applying
            </Button>
          </Item>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className={cn(wrap, "flex flex-wrap items-center gap-x-6 gap-y-3 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-4 text-[14px] text-muted")}>
      <Logo size={22} className="mr-auto" />
      <span className="flex flex-wrap items-center gap-x-6">
        <span className="tabular-nums">© {new Date().getFullYear()} Headcount</span>
        <a href="/jobs" className="py-2 transition-[color] duration-150 hover:text-ink">
          Jobs
        </a>
        <a href="/careers" className="py-2 transition-[color] duration-150 hover:text-ink">
          Careers
        </a>
        <a href="/privacy" className="py-2 transition-[color] duration-150 hover:text-ink">
          Privacy
        </a>
        <a href="/terms" className="py-2 transition-[color] duration-150 hover:text-ink">
          Terms
        </a>
        <a href="mailto:hello@itsheadcount.com" className="py-2 transition-[color] duration-150 hover:text-ink">
          Contact
        </a>
      </span>
    </footer>
  );
}
